import React, {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  createRef,
  useCallback,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import IndexRoute from './routes/index';
import setAuthToken from './helpers/utils';
import ReactGA from 'react-ga4';
import Hotjar from '@hotjar/browser';
import { footerLinksConfig } from './data/footerLinks';
import { setFooterNavigation } from './ducks/Global/actions';
import { setMobileView } from './ducks/Global/actions';
import ScrollToTop from './components/ScrollToTop';
import BackToTop from './components/BackToTop';
import { loadUser } from './ducks/Auth/actions';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNotificationPrompt } from './hooks/useNotificationPrompt';
import RouteSEO from './components/SEO/RouteSEO';
import { captureAttribution } from './helpers/analytics';
import { LegalFooter } from './components/Footer/LegalFooter';

// App shell — lazy-loaded so its Antd (and, via NavBar, Firebase) weight stays
// out of the eager main.js bundle. None of these render on the ad landing
// pages, and the modals mount only when their redux open-flag is truthy.
const NavBar = lazy(() => import('./components/NavBar'));
const Footer = lazy(() =>
  import('./components/Footer').then((m) => ({ default: m.Footer })),
);
const DrawerMenu = lazy(() =>
  import('./components/Drawer/index').then((m) => ({ default: m.DrawerMenu })),
);
const GlobalModal = lazy(() =>
  import('./components/Modal').then((m) => ({ default: m.GlobalModal })),
);
const RegisterModal = lazy(() =>
  import('./components/AuthModal/register').then((m) => ({
    default: m.RegisterModal,
  })),
);
const WelcomeModal = lazy(() =>
  import('./components/AuthModal/welcome').then((m) => ({
    default: m.WelcomeModal,
  })),
);
const SpecialOfferModal = lazy(() =>
  import('./components/Modal/specialOffer').then((m) => ({
    default: m.SpecialOfferModal,
  })),
);
const NotificationPrompt = lazy(() =>
  import('./components/NotificationPrompt'),
);

const siteId = 3690945;
const hotjarVersion = 6;

// Defer analytics until first interaction or 4s - improves FCP/TBT
const initDeferredAnalytics = () => {
  ReactGA.initialize('G-V5H4JJZZHV');
  Hotjar.init(siteId, hotjarVersion);
};

// Antd's full stylesheet is heavy and render-blocking, so it's deliberately kept
// OFF the Tailwind-based ad landing pages. Load it for every other (antd-based)
// route — login, dashboard, tools, admin, etc. Fired at module load for direct
// hits and again on navigation (see effect below); import() is cached so antd.css
// is only ever fetched once.
const ANTD_LEAN_PATHS = ['/', '/onsite', '/online', '/studio', '/8-week-program', '/programs'];
const loadAntdCssForApp = (pathname) => {
  if (!ANTD_LEAN_PATHS.includes(pathname)) {
    import('antd/dist/antd.css').catch(() => {});
  }
};
if (typeof window !== 'undefined') {
  loadAntdCssForApp(window.location.pathname);
}

const App = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // Load antd's stylesheet on antd-based app routes (kept off ad landing pages).
  useEffect(() => {
    loadAntdCssForApp(location.pathname);
  }, [location.pathname]);
  const isMobileView = useSelector((state) => state.global.isMobileView);
  const isDrawerOpen = useSelector((state) => state.global.isDrawerOpen);
  const isVisible = useSelector((state) => state.global.isLegalModalOpen);
  const legalTopic = useSelector((state) => state.global.legalTopic);
  const isAuthModalVisible = useSelector(
    (state) => state.auth.isAuthModalVisible
  );
  // Open-flags used to mount-gate the lazy shell modals (chunk loads only on open)
  const isSpecialModalOpen = useSelector(
    (state) => state.global.isSpecialModalOpen
  );
  const isGlobalModalOpen = useSelector(
    (state) => state.global.isGlobalModalOpen
  );
  const isWelcomeModalVisible = useSelector(
    (state) => state.global.isWelcomeModalVisible
  );
  const auth = useSelector((state) => state.auth);

  // Notification prompt hook
  const { showPrompt, promptContext, handlePromptClose } =
    useNotificationPrompt();

  // Capture ad/campaign attribution (UTMs, gclid, fbclid) on first landing
  useEffect(() => {
    captureAttribution();
  }, []);

  // Defer ReactGA and Hotjar until the first user interaction — keeps ~0.5MB of
  // analytics off the initial render path (FCP/LCP). Conversions still track:
  // submitting the lead form requires interaction, which loads analytics first.
  useEffect(() => {
    let loaded = false;
    const loadAnalytics = () => {
      if (loaded) return;
      loaded = true;
      initDeferredAnalytics();
    };
    const events = ['scroll', 'click', 'keydown', 'touchstart'];
    events.forEach((evt) =>
      window.addEventListener(evt, loadAnalytics, { once: true, passive: true })
    );
    return () => {
      events.forEach((evt) =>
        window.removeEventListener(evt, loadAnalytics)
      );
    };
  }, []);

  // Memoize the links array
  const links = useMemo(
    () => footerLinksConfig[location.pathname] || [],
    [location.pathname]
  );

  const sectionsRefs = useMemo(() => {
    const refs = {};
    links.forEach((link) => {
      refs[link.link] = createRef();
    });
    return refs;
  }, [links]);

  // Load user authentication
  useEffect(() => {
    const token = localStorage.getItem('token');
    const isResetPassword = location.pathname.includes('/reset-password');

    if (auth.isAuthenticated === null && !isResetPassword) {
      if (token) {
        setAuthToken(token);
        dispatch(loadUser());
      } else {
        // No token, set userLoading to false immediately
        dispatch({ type: 'LOAD_USER_ERROR', errors: [] });
      }
    }
  }, [dispatch, auth.isAuthenticated, location.pathname]);

  // Lazy-load Firebase messaging only for logged-in users - improves initial load
  useEffect(() => {
    if (!auth.isAuthenticated) return;

    let unsubscribe;
    const setupMessaging = async () => {
      const { messaging, onMessage } = await import('./firebase/firebase');
      if (!messaging) return;
      unsubscribe = onMessage(messaging, (payload) => {
        const handleNotificationClick = () => {
          const url = payload.data?.url || '/dashboard/videos/all';
          navigate(url);
        };
        toast.info(
          <div onClick={handleNotificationClick} style={{ cursor: 'pointer' }}>
            <strong>{payload.notification?.title || 'New Notification'}</strong>
            <br />
            {payload.notification?.body}
          </div>,
          {
            className: 'custom-toast',
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            onClick: handleNotificationClick,
          }
        );
      });
    };
    setupMessaging();
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [auth.isAuthenticated, navigate]);

  // Update axios with auth token
  useEffect(() => {
    if (auth.token) {
      setAuthToken(auth.token);
    }
  }, [auth.token]);

  // Set footer navigation
  useEffect(() => {
    dispatch(setFooterNavigation(links));
  }, [dispatch, links]);

  // Handle window resize
  useEffect(() => {
    const checkWindowSize = () => {
      dispatch(setMobileView(window.innerWidth <= 768));
    };

    checkWindowSize(); // Check initial size
    window.addEventListener('resize', checkWindowSize);

    return () => {
      window.removeEventListener('resize', checkWindowSize);
    };
  }, [dispatch]);

  // Google Analytics tracking
  useEffect(() => {
    const currentPath = location.pathname + location.search;
    ReactGA.gtag('config', 'G-V5H4JJZZHV', {
      page_path: currentPath,
    });
  }, [location]);

  const handleScroll = useCallback((ref) => {
    if (ref.current) {
      window.scrollTo({
        top: ref.current.offsetTop,
        behavior: 'smooth',
      });
    }
  }, []);

  // Handle scroll to section from navigation
  useEffect(() => {
    if (location.pathname === '/' && location.state?.scrollTo) {
      const ref = sectionsRefs[location.state.scrollTo];
      if (ref?.current) {
        handleScroll(ref);
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [handleScroll, location, sectionsRefs, navigate]);

  const getRouteBackground = (pathname) => {
    if (pathname.startsWith('/dashboard/videos') || pathname.startsWith('/videos')) {
      return 'bg-video-library dark';
    }
    if (pathname.startsWith('/dashboard') || pathname.startsWith('/booking')) {
      return 'bg-video-library dark';
    }
    return '';
  };

  // ✅ SIMPLIFIED: Cleaner route checking
  const hideNavAndFooter = useMemo(() => {
    const hiddenRoutes = [
      '/login',
      '/register',
      '/reset-password',
      '/live-stream',
      '/program-checkout',
      '/8-week-program',
      '/12-week-program',
      '/onsite',
      '/online',
      '/studio',
      '/free-meal-plan',
      '/6-week-challenge',
      '/links',
      '/reset-starter',
      '/free',
      '/apply',
    ];

    return (
      hiddenRoutes.includes(location.pathname) ||
      location.pathname.startsWith('/watch-stream') ||
      // Meal prep pages have their own header.
      location.pathname === '/meals' ||
      location.pathname.startsWith('/meals/') ||
      location.pathname.includes('/watch-video') ||
      // The workout PLAYER (/workouts/:id) is full-screen chrome-free; the
      // picker (/workouts) keeps the nav.
      location.pathname.startsWith('/workouts/')
    );
  }, [location.pathname]);

  // Show footer on home landing, onsite, online landing, and nutrition pages
  const showFooter = useMemo(() => {
    const showFooterRoutes = ['/', '/onsite', '/online', '/studio', '/programs'];
    const isNutritionRoute =
      location.pathname.startsWith('/nutrition') ||
      location.pathname.startsWith('/dashboard/nutrition');
    return showFooterRoutes.includes(location.pathname) || isNutritionRoute;
  }, [location.pathname]);

  const isNutritionRoute =
    location.pathname.startsWith('/nutrition') ||
    location.pathname.startsWith('/dashboard/nutrition');

  const landingPaths = ['/', '/onsite', '/online', '/studio', '/8-week-program', '/programs'];
  const navBarTransparent =
    landingPaths.includes(location.pathname) && !auth.isAuthenticated;

  const backgroundClass = getRouteBackground(location.pathname);

  return (
    <div
      className={`app ${backgroundClass} ${isNutritionRoute ? 'nutrition-route' : ''}`}
    >
      <RouteSEO />
      <ToastContainer />
      <ScrollToTop />

      {/* Modals — mount only when their open-flag is truthy, so the lazy chunk
          never loads on a cold landing-page paint. Each gets its own
          fallback={null} Suspense (never a shared/visible one). */}
      {isSpecialModalOpen && (
        <Suspense fallback={null}>
          <SpecialOfferModal
            sectionsRefs={sectionsRefs}
            handleScroll={handleScroll}
          />
        </Suspense>
      )}
      {isGlobalModalOpen && (
        <Suspense fallback={null}>
          <GlobalModal isVisible={isVisible} content={legalTopic} />
        </Suspense>
      )}
      {isAuthModalVisible && (
        <Suspense fallback={null}>
          <RegisterModal isVisible={isAuthModalVisible} />
        </Suspense>
      )}
      {isWelcomeModalVisible && (
        <Suspense fallback={null}>
          <WelcomeModal />
        </Suspense>
      )}
      {showPrompt && (
        <Suspense fallback={null}>
          <NotificationPrompt
            visible={showPrompt}
            context={promptContext}
            onClose={handlePromptClose}
          />
        </Suspense>
      )}

      {/* Navigation — height-reserving fallback avoids CLS while the chunk loads */}
      {!hideNavAndFooter && (
        <Suspense fallback={<div style={{ minHeight: 64 }} />}>
          <NavBar
            isMobileView={isMobileView}
            isDrawerOpen={isDrawerOpen}
            sectionsRefs={sectionsRefs}
            handleScroll={handleScroll}
            variant={navBarTransparent ? 'transparent' : 'dark'}
            minimal={isNutritionRoute && !auth.isAuthenticated}
          />
        </Suspense>
      )}

      {/* Mobile Drawer - hidden on nutrition (no nav to open it) */}
      {isMobileView && !isNutritionRoute && (
        <Suspense fallback={null}>
          <DrawerMenu />
        </Suspense>
      )}

      {/* Main Content — deliberately NOT inside a shell Suspense */}
      <IndexRoute
        auth={auth}
        sectionsRefs={sectionsRefs}
        handleScroll={handleScroll}
      />

      {/* Footer — slim legal-only footer on the focused nutrition tool,
          full marketing footer everywhere else it shows. */}
      {showFooter &&
        (isNutritionRoute ? (
          <LegalFooter />
        ) : (
          <Suspense fallback={null}>
            <Footer sectionsRefs={sectionsRefs} handleScroll={handleScroll} />
          </Suspense>
        ))}

      {/* Back to Top Button — on every page. Landings with a fixed buy bar or
          an exit-intent overlay move/hide it from their own CSS. */}
      <BackToTop showAfter={400} />
    </div>
  );
};

export default App;
